import { Copy, Play, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useCreateWebhookTemplate,
  useDeleteWebhookTemplate,
  useTestWebhookTemplate,
} from "@/hooks/useWebhooks";
import type {
  WebhookTemplate,
  WebhookTemplateCreateRequest,
  WebhookTemplateTestResponse,
} from "@/lib/api";
import { cn } from "@/lib/utils";

const PROVIDERS = ["generic", "tradingview", "tradeideas", "trendspider"];

export function WebhookTemplateSection({
  templates,
  loading,
}: {
  templates: WebhookTemplate[];
  loading: boolean;
}) {
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  return (
    <section className="mt-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Webhook templates</h2>
          <p className="text-xs text-muted-fg">
            Built-in and custom payload formats used by webhook configs.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreatingTemplate(true)}>
          <Plus />
          New template
        </Button>
      </div>
      {creatingTemplate ? (
        <WebhookTemplateForm
          onCancel={() => setCreatingTemplate(false)}
          onSaved={() => setCreatingTemplate(false)}
        />
      ) : null}
      <WebhookTemplateTestConsole templates={templates} />
      <WebhookTemplateList templates={templates} loading={loading} />
    </section>
  );
}

function WebhookTemplateTestConsole({ templates }: { templates: WebhookTemplate[] }) {
  const [provider, setProvider] = useState("tradingview");
  const providerTemplates = templates.filter((template) => template.provider === provider);
  const [templateId, setTemplateId] = useState("");
  const selectedTemplate = providerTemplates.find((template) => template.id === templateId) ?? providerTemplates[0];
  const [contentType, setContentType] = useState("application/json");
  const [body, setBody] = useState("");
  const [result, setResult] = useState<WebhookTemplateTestResponse | null>(null);
  const test = useTestWebhookTemplate();

  const loadTemplate = (template: WebhookTemplate | undefined) => {
    if (!template) return;
    setTemplateId(template.id);
    setContentType(template.content_type);
    setBody(template.body);
  };

  const run = () => {
    setResult(null);
    test.mutate(
      {
        provider,
        body,
        content_type: contentType,
        default_template_id: selectedTemplate?.id ?? null,
      },
      {
        onSuccess: setResult,
        onError: (err) => toast.error(`test failed: ${err.message}`),
      },
    );
  };

  return (
    <section className="mb-3 rounded-md border border-border/80 bg-muted/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Template test console</h3>
          <p className="text-xs text-muted-fg">Preview payload parsing without creating inbox rows.</p>
        </div>
        <Button size="sm" disabled={test.isPending || !body.trim()} onClick={run}>
          <Play className="size-3.5" />
          Test
        </Button>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-[220px_1fr]">
        <div className="grid content-start gap-3">
          <Field label="Provider">
            <select
              className={inputClass}
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                setTemplateId("");
                setResult(null);
              }}
            >
              {PROVIDERS.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </Field>
          <Field label="Template">
            <select
              className={inputClass}
              value={selectedTemplate?.id ?? ""}
              onChange={(e) => loadTemplate(providerTemplates.find((template) => template.id === e.target.value))}
            >
              {providerTemplates.map((template) => (
                <option key={template.id} value={template.id}>{template.display_name}</option>
              ))}
            </select>
          </Field>
          <Field label="Content type">
            <input className={inputClass} value={contentType} onChange={(e) => setContentType(e.target.value)} />
          </Field>
          <Button
            size="sm"
            variant="outline"
            disabled={!selectedTemplate}
            onClick={() => loadTemplate(selectedTemplate)}
          >
            <Copy className="size-3.5" />
            Load sample
          </Button>
        </div>
        <div className="grid gap-3">
          <textarea
            className="min-h-28 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs text-fg outline-none focus:border-accent"
            placeholder="Paste or load a webhook payload..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {result ? <WebhookTemplateTestResult result={result} /> : null}
        </div>
      </div>
    </section>
  );
}

function WebhookTemplateTestResult({ result }: { result: WebhookTemplateTestResponse }) {
  return (
    <section className="rounded-md border border-border/70 bg-bg p-3">
      <div className="flex flex-wrap gap-2 text-[11px]">
        <Badge variant="outline">{result.provider}</Badge>
        <Badge variant="secondary">{result.item_type ?? "unknown"}</Badge>
        {result.template ? <Badge variant="outline">{textValue(result.template.id)}</Badge> : null}
        {result.detected_symbols.map((symbol) => (
          <Badge key={symbol} variant="outline">{symbol}</Badge>
        ))}
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        <ResultBlock label="Preview" value={result.raw_text_preview} />
        <ResultBlock label="Structured payload" value={jsonValue(result.structured_payload)} />
        <ResultBlock label="Template" value={jsonValue(result.template)} />
        <ResultBlock label="Metadata" value={jsonValue(result.metadata)} />
      </div>
    </section>
  );
}

function ResultBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-fg">{label}</div>
      <pre className="max-h-36 overflow-auto whitespace-pre-wrap rounded border border-border/70 bg-muted/10 px-2 py-1.5 text-[11px] leading-4 text-fg">
        {value || "-"}
      </pre>
    </div>
  );
}

function WebhookTemplateList({
  templates,
  loading,
}: {
  templates: WebhookTemplate[];
  loading: boolean;
}) {
  const del = useDeleteWebhookTemplate();
  if (loading) return <div className="text-xs text-muted-fg">Loading templates...</div>;
  return (
    <div className="grid gap-2 xl:grid-cols-2">
      {templates.map((template) => (
        <section key={template.id} className="rounded-md border border-border bg-muted/5 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate text-sm font-medium">{template.display_name}</h3>
                <Badge variant="outline" className="text-[10px]">{template.provider}</Badge>
                <Badge variant={template.built_in ? "secondary" : "outline"} className="text-[10px]">
                  {template.built_in ? "built-in" : "custom"}
                </Badge>
                {!template.enabled ? <Badge variant="outline" className="text-[10px]">disabled</Badge> : null}
              </div>
              <code className="mt-1 block text-[11px] text-muted-fg">{template.id}</code>
            </div>
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                title="Copy template body"
                onClick={() => void copy(template.body)}
              >
                <Copy className="size-3.5" />
              </Button>
              {!template.built_in ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7 text-muted-fg"
                  title="Delete template"
                  disabled={del.isPending}
                  onClick={() => {
                    if (!confirm(`Delete webhook template '${template.id}'?`)) return;
                    del.mutate(template.id, {
                      onSuccess: () => toast.success(`deleted ${template.id}`),
                      onError: (err) => toast.error(`delete failed: ${err.message}`),
                    });
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              ) : null}
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-fg">
            <span>{template.content_type}</span>
            <span>{template.parser_mode}</span>
            <span>{template.template_key}.{template.template_version}</span>
          </div>
          <code className="mt-2 block max-h-24 overflow-auto rounded border border-border/70 bg-bg px-2 py-1.5 text-[11px] leading-4 text-fg">
            {template.body}
          </code>
        </section>
      ))}
      {templates.length === 0 ? (
        <section className="rounded-md border border-border bg-muted/10 p-4 text-xs text-muted-fg xl:col-span-2">
          No webhook templates yet.
        </section>
      ) : null}
    </div>
  );
}

function WebhookTemplateForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const create = useCreateWebhookTemplate();
  const [form, setForm] = useState({
    id: "",
    provider: "generic",
    template_key: "",
    template_version: "v1",
    display_name: "",
    content_type: "text/plain",
    parser_mode: "auto",
    body: "",
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const submit = () => {
    const body: WebhookTemplateCreateRequest = {
      id: form.id,
      provider: form.provider,
      template_key: form.template_key,
      template_version: form.template_version,
      display_name: form.display_name,
      content_type: form.content_type,
      parser_mode: form.parser_mode,
      body: form.body,
      enabled: true,
      metadata: {},
    };
    create.mutate(body, {
      onSuccess: () => {
        toast.success(`created ${form.id}`);
        onSaved();
      },
      onError: (err) => toast.error(`create failed: ${err.message}`),
    });
  };
  return (
    <section className="mb-3 rounded-md border border-border bg-muted/10 p-4">
      <h3 className="text-sm font-semibold">New webhook template</h3>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <Field label="id">
          <input className={inputClass} value={form.id} onChange={(e) => set("id", e.target.value)} />
        </Field>
        <Field label="Display name">
          <input className={inputClass} value={form.display_name} onChange={(e) => set("display_name", e.target.value)} />
        </Field>
        <Field label="Provider">
          <select className={inputClass} value={form.provider} onChange={(e) => set("provider", e.target.value)}>
            {PROVIDERS.map((provider) => (
              <option key={provider} value={provider}>{provider}</option>
            ))}
          </select>
        </Field>
        <Field label="Template key">
          <input className={inputClass} value={form.template_key} onChange={(e) => set("template_key", e.target.value)} />
        </Field>
        <Field label="Version">
          <input className={inputClass} value={form.template_version} onChange={(e) => set("template_version", e.target.value)} />
        </Field>
        <Field label="Content type">
          <input className={inputClass} value={form.content_type} onChange={(e) => set("content_type", e.target.value)} />
        </Field>
        <Field label="Parser mode">
          <select className={inputClass} value={form.parser_mode} onChange={(e) => set("parser_mode", e.target.value)}>
            <option value="auto">auto</option>
            <option value="json">json</option>
            <option value="kv_text">kv_text</option>
            <option value="raw_text">raw_text</option>
          </select>
        </Field>
        <Field label="Body" className="md:col-span-2">
          <textarea
            className="min-h-24 w-full rounded-md border border-border bg-bg px-2 py-2 text-xs text-fg outline-none focus:border-accent"
            value={form.body}
            onChange={(e) => set("body", e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={create.isPending} onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={create.isPending} onClick={submit}>Create</Button>
      </div>
    </section>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("text-xs", className)}>
      <span className="mb-1 block text-muted-fg">{label}</span>
      {children}
    </label>
  );
}

async function copy(value: string) {
  await navigator.clipboard.writeText(value);
  toast.success("copied");
}

function jsonValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  return JSON.stringify(value, null, 2);
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

const inputClass =
  "h-8 w-full rounded-md border border-border bg-bg px-2 text-xs text-fg outline-none focus:border-accent";
